import React, { useRef } from 'react';
import PropTypes, { oneOf } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  NamedRedirect,
  FieldRadioButtonGroup,
  FieldCustomSelect,
  FieldCheckbox,
  InlineTextButton,
} from '../../components';
import css from './WorkerExpForm.module.css';
import {
  employmentType,
  languageSelectOptions,
  listingTypes,
  radioButtonType,
  workingStatus as workStatus,
} from '../../marketplace-custom-config';
import { getMonthFullNames } from '../../util/dates';
import { range } from '../../util/data';
import { useMemo } from 'react';

const nativeLangOptions = Object.keys(languageSelectOptions);
const textfield = 'textfield';
const errorTextField = 'errorTextField';
const maxDescLength = 500;
const minDescLength = 50;
const currentlyWorking = workStatus.currentlyWorking;
var yearsArray = Array.from({ length: 1990 - 1924 }, (_, index) => (1924 + index).toString());

const currentYear = new Date().getFullYear();
const prevYear = currentYear - 100;
const nextYear = currentYear;
const yearOptions = range(prevYear, nextYear).map(year => ({
  label: year.toString(),
  value: year.toString(),
}));

const getFilteredYear = yearOptions.filter(y => !yearsArray.includes(y.value));
// getFilteredYear.push({ value: 'before 1990', label: 'before 1990' });

const monthOptions = getMonthFullNames();

const useMemorizedFilter = (refValue, options, filterCb) => {
  const filteredOptions = useMemo(() => (!!refValue ? options.filter(filterCb) : []), [
    refValue,
    options.length,
  ]);
  return filteredOptions;
};

const WorkerExpFormComponent = props => {
  const { onSubmit, ...restProps } = props;
  const formApi = useRef(null);
  const handleSubmit = values => {
    const {
      companyName,
      jobType,
      industryName: savedIndustryName,
      jobDesc,
      jobTitle,
      startMonth: savedStartMonth,
      startYear: savedStartYear,
      endMonth: savedEndMonth,
      endYear: savedEndYear,
      workingStatus = [],
      id,
    } = values;
    const industryName =
      typeof savedIndustryName === 'string' ? { value: savedIndustryName } : savedIndustryName;
    const endYear = typeof savedEndYear === 'string' ? { value: savedEndYear } : savedEndYear;
    const endMonth = typeof savedEndMonth == 'string' ? { value: savedEndMonth } : savedEndMonth;
    const startYear =
      typeof savedStartYear === 'string' ? { value: savedStartYear } : savedStartYear;
    const startMonth =
      typeof savedStartMonth == 'string' ? { value: savedStartMonth } : savedStartMonth;

    const endDatesMaybe = {
      ...(endYear && endYear.value ? { endYear: endYear.value } : {}),
      ...(endMonth && endMonth.value ? { endMonth: endMonth.value } : {}),
      ...(endYear && endYear.value && endMonth && endMonth.value
        ? { jobEndDate: new Date(parseInt(endYear.value), parseInt(endMonth.value) - 1, 1).toISOString() }
        : {}),
    };
    const jobStartDate = new Date(parseInt(startYear.value), parseInt(startMonth.value) - 1, 1).toISOString();
    const idMayBe = !!id ? { id } : {};
    const updatedValues = {
      companyName,
      jobType,
      industryName: industryName.value,
      jobDesc,
      jobTitle,
      startMonth: startMonth.value,
      startYear: startYear.value,
      jobStartDate,
      workingStatus:
        workingStatus[0] === workStatus.currentlyWorking
          ? currentlyWorking
          : workStatus.leftCompany,
      ...endDatesMaybe,
      ...idMayBe,
    };
    formApi.current && formApi.current.restart();
    onSubmit(updatedValues);
  };
  return (
    <FinalForm
      {...restProps}
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          inProgress,
          invalid,
          values,
          form,
          userNativeLang,
          intl,
          pristine,
          onCancel,
          handleSubmit,
        } = formRenderProps;

        formApi.current = form;
        const {
          startMonth: savedStartMonth,
          startYear: savedStartYear,
          workingStatus = [],
          endYear: savedEndYear,
        } = values;
        const startMonth =
          typeof savedStartMonth === 'string' ? { value: savedStartMonth } : savedStartMonth;
        const startYear =
          typeof savedStartYear === 'string' ? { value: savedStartYear } : savedStartYear;
        const endYear = typeof savedEndYear === 'string' ? { value: savedEndYear } : savedEndYear;

        const curntYear =
          endYear && endYear.value ? parseInt(endYear.value) : new Date().getFullYear();

        const jobStartDate =
          startYear && !!startYear.value && startMonth && !!startMonth.value
            ? new Date(`1/${startMonth.value}/${startYear.value}`)
            : null;
        const isValidJobStartDate = !!jobStartDate && jobStartDate instanceof Date;
        const isEndDateAvailable =
          isValidJobStartDate &&
          (!workingStatus.length ||
            (workingStatus.length > 0 && workingStatus[0] !== currentlyWorking));

        const submitDisabled = invalid || inProgress || pristine;

        const selectedStartMonthIndx =
          startMonth && !!startMonth.value
            ? parseInt(startYear?.value || 0) === curntYear
              ? monthOptions.findIndex(option => option.value === startMonth.value)
              : -1
            : -1;
        const endYearOptions = useMemorizedFilter(
          startYear && startYear.value,
          // yearOptions,
          getFilteredYear,
          opiton => parseInt(opiton.value) >= parseInt(startYear.value)
        );

        const endMnthRefValue = `${startMonth && startMonth.value}-${startYear &&
          startYear.value}-${endYear && endYear.value}`;

        const endMonthOptions = useMemorizedFilter(
          endMnthRefValue,
          monthOptions,
          (_, indx) => indx >= selectedStartMonthIndx
        );

        //employment type
        const empTypeLabel = intl.formatMessage({ id: 'WorkerExpForm.empTypeLabel' });
        const empTypeRequiredMessage = intl.formatMessage({ id: 'WorkerExpForm.empTypeRequired' });
        const empTypeValidator = validators.required(empTypeRequiredMessage);
        const empTypes = Object.values(employmentType).map(emp => ({
          label: emp.label[userNativeLang],
          key: emp.value,
        }));

        //company name
        const companyNameLabel = intl.formatMessage({ id: 'WorkerExpForm.companyNameLabel' });
        const companyNamePlaceholder = intl.formatMessage({
          id: 'WorkerExpForm.companyNamePlaceholder',
        });
        const companyNameRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.companyNameRequired',
        });
        const companyNameValidator = validators.required(companyNameRequiredMessage);

        //job title
        const jobTitleLabel = intl.formatMessage({ id: 'WorkerExpForm.jobTitleLabel' });
        const jobTitlePlaceholder = intl.formatMessage({
          id: 'WorkerExpForm.jobTitlePlaceholder',
        });
        const jobTitleRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.jobTitleRequired',
        });
        const jobTitleValidator = validators.required(jobTitleRequiredMessage);

        //industry type
        const industryTypeLabel = intl.formatMessage({ id: 'WorkerExpForm.industryTypeLabel' });
        const industryTypePlaceholder = intl.formatMessage({
          id: 'WorkerExpForm.industryTypePlaceholder',
        });
        const industryTypeRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.industryTypeRequired',
        });
        const industryTypeValidator = validators.required(industryTypeRequiredMessage);

        //start date label
        const startDateLabel = intl.formatMessage({ id: 'WorkerExpForm.startDateLabel' });

        //end date label
        const endDateLabel = intl.formatMessage({ id: 'WorkerExpForm.endDateLabel' });

        // start month
        const startMonthPlaceholder = intl.formatMessage({
          id: 'WorkerExpForm.startMonthPlaceholder',
        });

        const startMonthRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.startMonthRequired',
        });
        const startMonthValidator = validators.required(startMonthRequiredMessage);

        // start year
        const startYearPlaceholder = intl.formatMessage({
          id: 'WorkerExpForm.startYearPlaceholder',
        });

        const startYearRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.startYearRequired',
        });
        const startYearValidator = validators.required(startYearRequiredMessage);

        //end month
        const endMonthRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.endMonthRequired',
        });
        const endMonthValidator =
          !!workingStatus.length && workingStatus[0] === currentlyWorking
            ? null
            : validators.required(endMonthRequiredMessage);

        //end year
        const endYearRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.endYearRequired',
        });
        const endYearValidator =
          !!workingStatus.length && workingStatus[0] === currentlyWorking
            ? null
            : validators.required(endYearRequiredMessage);

        //job Description
        const jobDescLabel = intl.formatMessage({ id: 'WorkerExpForm.jobDescLabel' });
        const jobDescPlaceholder = intl.formatMessage({
          id: 'WorkerExpForm.jobDescPlaceholder',
        });
        const jobDescRequiredMessage = intl.formatMessage({
          id: 'WorkerExpForm.jobDescRequired',
        });
        const jobDescMaxLengthMessage = intl.formatMessage(
          {
            id: 'WorkerExpForm.jobDescMaxLength',
          },
          { maxLength: maxDescLength }
        );
        const jobDescMinLengthMessage = intl.formatMessage(
          {
            id: 'WorkerExpForm.jobDescMinLength',
          },
          { minLength: minDescLength }
        );
        const jobDescValidator = validators.composeValidators(
          validators.required(jobDescRequiredMessage),
          validators.maxLength(jobDescMaxLengthMessage, maxDescLength),
          validators.minLength(jobDescMinLengthMessage, minDescLength)
        );

        // current company
        const currentCompanyLabel = intl.formatMessage({ id: 'WorkerExpForm.currentCompanyLabel' });

        const checkAndHandleOnChange = (isDisabled, name, value) => {
          if (isDisabled) return;
          if (name == 'workingStatus') {
            form.batch(() => {
              form.change('endYear', undefined);
              form.change('endMonth', undefined);
              form.change(name, value);
            });
            form.resetFieldState('endYear');
            form.resetFieldState('endMonth');
          } else if (name === 'endYear') {
            form.batch(() => {
              form.change('endMonth', undefined);
              form.change(name, value);
            });
          } else {
            form.change(name, value);
          }
        };

        const handleCancel = e => {
          e.preventDefault();
          e.stopPropagation();
          form.restart();
          if (typeof onCancel === 'function') {
            onCancel(e);
          }
        };

        const clasess = classNames(css.root || rootClassName, className);

        return (
          <Form className={clasess} onSubmit={handleSubmit}>
            <h2>
              <FormattedMessage id={'WorkerExpForm.heading'} />
            </h2>
            <div>
              {' '}
              <FieldRadioButtonGroup
                id={formId ? `${formId}.jobType` : 'jobType'}
                label={empTypeLabel}
                validate={empTypeValidator}
                options={empTypes}
                name="jobType"
                radioButtonType={radioButtonType.filled}
                listClassName={css.empTypeList}
                itemClassName={css.empTypeItem}
              />
            </div>
            <div>
              <FieldTextInput
                id={formId ? `${formId}.companyName` : 'companyName'}
                label={companyNameLabel}
                placeholder={companyNamePlaceholder}
                type="text"
                validate={companyNameValidator}
                name="companyName"
                className={textfield}
                errorClassName={errorTextField}
              />
            </div>
            <div>
              <FieldTextInput
                id={formId ? `${formId}.jobTitle` : 'jobTitle'}
                label={jobTitleLabel}
                placeholder={jobTitlePlaceholder}
                type="text"
                validate={jobTitleValidator}
                name="jobTitle"
                className={textfield}
                errorClassName={errorTextField}
              />
            </div>
            <div>
              <FieldCustomSelect
                id={formId ? `${formId}.industryName` : 'industryName'}
                label={industryTypeLabel}
                placeholder={industryTypePlaceholder}
                options={Object.values(listingTypes)}
                validate={industryTypeValidator}
                name="industryName"
                className={textfield}
                errorClassName={errorTextField}
                userNativeLang={userNativeLang}
                isSearchable={true}
              />
            </div>
            <div>
              <label className={css.dateLabel}>{startDateLabel}</label>
              <div className={css.StartDateDiv}>
                <FieldCustomSelect
                  id={formId ? `${formId}.startYear` : 'startYear'}
                  name="startYear"
                  placeholder={startYearPlaceholder}
                  className={textfield}
                  errorClassName={errorTextField}
                  validate={startYearValidator}
                  // options={yearOptions}
                  options={getFilteredYear}
                  userNativeLang={userNativeLang}
                  isSearchable={true}
                />
                <FieldCustomSelect
                  id={formId ? `${formId}.startMonth` : 'startMonth'}
                  name="startMonth"
                  placeholder={startMonthPlaceholder}
                  className={textfield}
                  errorClassName={errorTextField}
                  validate={startMonthValidator}
                  options={monthOptions}
                  userNativeLang={userNativeLang}
                  isSearchable={true}
                />
              </div>
            </div>
            <div
              className={classNames({
                [css.disabledDateWrapper]: !isEndDateAvailable,
              })}
            >
              <label className={css.dateLabel}>{endDateLabel}</label>
              <div className={css.EndDateDiv}>
                {' '}
                <FieldCustomSelect
                  id={formId ? `${formId}.endYear` : 'endYear'}
                  name="endYear"
                  placeholder={startYearPlaceholder}
                  className={textfield}
                  errorClassName={errorTextField}
                  validate={endYearValidator}
                  options={endYearOptions}
                  onChange={checkAndHandleOnChange.bind(null, !isEndDateAvailable, 'endYear')}
                  disabled={!isEndDateAvailable}
                  reRenderOn={`${workingStatus.length}-${startYear && startYear.value}`}
                  userNativeLang={userNativeLang}
                // isSearchable={true}
                />
                <FieldCustomSelect
                  id={formId ? `${formId}.endMonth` : 'endMonth'}
                  name="endMonth"
                  placeholder={startMonthPlaceholder}
                  className={textfield}
                  errorClassName={errorTextField}
                  validate={endMonthValidator}
                  options={endMonthOptions}
                  onChange={checkAndHandleOnChange.bind(null, !isEndDateAvailable, 'endMonth')}
                  disabled={!isEndDateAvailable}
                  reRenderOn={`${workingStatus.length}-${startMonth &&
                    startMonth.value}-${selectedStartMonthIndx}`}
                  userNativeLang={userNativeLang}
                />
              </div>
            </div>
            <div className={classNames({ [css.disabledDateWrapper]: !isValidJobStartDate })}>
              <FieldCheckbox
                id={formId ? `${formId}.workingStatus` : 'workingStatus'}
                name="workingStatus"
                label={currentCompanyLabel}
                value={currentlyWorking}
                onChange={checkAndHandleOnChange.bind(null, !isValidJobStartDate, 'workingStatus')}
              />
            </div>

            <div>
              <FieldTextInput
                id={formId ? `${formId}.jobDesc` : 'jobDesc'}
                label={jobDescLabel}
                placeholder={jobDescPlaceholder}
                type="textarea"
                validate={jobDescValidator}
                name="jobDesc"
                className={textfield}
                errorClassName={errorTextField}
              />
            </div>
            <div className={css.btncomp}>
              <PrimaryButton type="submit" disabled={submitDisabled} className={css.submitButton}>
                {intl.formatMessage({ id: 'WorkerExpForm.submitBtn' })}
              </PrimaryButton>
              <InlineTextButton type="button" className={css.CancelBtn} onClick={handleCancel}>
                {intl.formatMessage({ id: 'WorkerExpForm.cancelBtn' })}
              </InlineTextButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

WorkerExpFormComponent.defaultProps = {
  inProgress: false,
  userNativeLang: languageSelectOptions.en.value,
};

const { bool, func } = PropTypes;

WorkerExpFormComponent.propTypes = {
  inProgress: bool,

  // from injectIntl
  intl: intlShape.isRequired,
  userNativeLang: oneOf(nativeLangOptions).isRequired,
  onCancel: func.isRequired,
};

const WorkerExpForm = compose(injectIntl)(WorkerExpFormComponent);
WorkerExpForm.displayName = 'WorkerExpForm';

export default WorkerExpForm;
